import AddIcon from "@mui/icons-material/Add";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {Alert, Box, Button, LinearProgress, Skeleton} from "@mui/material";
import {keyframes} from "@mui/system";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {getErrorMessage} from "crqlar-react-common/dist/utils/http.util";
import {useAllTaxRates} from "crqlar-react-common/dist/utils/hooks/rates/useTaxRates";
import {useAllServiceCategories} from "crqlar-react-common/dist/utils/hooks/services/useAllServiceCategories";
import {ReactNode, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {FOLIO_COLUMN_GAP, FOLIO_COLUMNS, FOLIO_LINE_COLUMNS, FOLIO_LINES_MIN_WIDTH, FOLIO_ROW_HEIGHT} from "./FolioCellBox";
import FolioDiscountDialog from "./FolioDiscountDialog";
import {DraftLine} from "./folioDraft.util";
import {useFolioFormat} from "./folioFormat";
import {folioTotals} from "./folioLine.util";
import FolioLineRow from "./FolioLineRow";
import FolioMarketCodeDialog from "./FolioMarketCodeDialog";
import FolioPendingTipRow from "./FolioPendingTipRow";
import FolioSummary from "./FolioSummary";
import {fetchLineRate, LineRate} from "./lineRate";
import {serviceAutofill} from "./serviceAutofill";
import {FolioDraft} from "./useFolioDraft";
import {useLineArrivals} from "./useLineArrivals";

const GRID_SX = {display: 'grid', gridTemplateColumns: FOLIO_LINE_COLUMNS, columnGap: FOLIO_COLUMN_GAP, alignItems: 'center'};

// Lines that arrive quickly (read ahead, or a fast server) never flash the bar or the placeholder:
// they fade in late
const appear = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;
const APPEAR_LATE_SX = {opacity: 0, animation: `${appear} 150ms ease-out 250ms forwards`};
const LOADING_BAR_SX = {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -1,
    height: 2,
    ...APPEAR_LATE_SX,
} as const;

// The totals' height below the lines: subtotal, VAT, tip, the rule and the total, with their padding
const TOTALS_HEIGHT = 150;

// Beside the lines when they scroll sideways (a phone): it stays in view
const STAY_IN_VIEW_SX = {position: 'sticky', left: 0} as const;

interface Props {
    draft: FolioDraft
    /** The folio's lines can change now. */
    editable: boolean
    currency: string
    services: Service[]
    /** The reservation's service, for its line when the sector's list doesn't carry it. */
    reservationService?: Service
    /** Casablanca is available: every line needs a VAT rate, a deliberate 0 % is offered. */
    vatRequired: boolean
    /** Package lines that stay hidden (they are still saved and counted). */
    hiddenIds: Set<string>
    /** The reservation's day: a line shows its date only when it's another one. */
    referenceDay?: Date
    /** Where a new line starts and ends: the reservation's time. */
    newLine: {start: Date, end: Date}
    /** The stored total of a folio that can't change. */
    storedTotalCents?: number
    /** The lines couldn't be read: a retry instead of the loading bar. */
    loadError?: boolean
    onRetry?: () => void
    /** Left of the totals, where a draft has "Add line": a finalized folio's payment. */
    payment?: ReactNode
    /**
     * The tip the guest signed, until Confirm and charge posts it: a faded line after the lines and a
     * faded row in the totals - never part of the draft.
     */
    pendingTipCents?: number
}

/**
 * While the lines load: room for two lines and the totals - a folio's usual content, which the
 * dialog's least height holds -, so the dialog opens at the height it keeps once they are there.
 */
const LinesPlaceholder = () => (
    <Box aria-hidden sx={APPEAR_LATE_SX}>
        {[60, 45].map(width => (
            <Box key={width} sx={{...GRID_SX, height: FOLIO_ROW_HEIGHT, borderBottom: 1, borderColor: 'divider'}}>
                <Skeleton variant={'rounded'} height={14} sx={{mx: 1, width: `${width}%`}}/>
                {/* In the Total column */}
                <Skeleton variant={'rounded'} width={56} height={14} sx={{gridColumn: '-3 / -2', justifySelf: 'end', mr: 1}}/>
            </Box>
        ))}
        {/* Where the summary will end: at the Total column */}
        <Box sx={{...GRID_SX, alignItems: 'start', height: TOTALS_HEIGHT, pt: 2.5}}>
            <Box sx={{gridColumn: '3 / -2', justifySelf: 'end', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5, pr: 1}}>
                {[200, 200, 200, 220].map((width, index) => <Skeleton key={index} variant={'rounded'} width={width} height={14}/>)}
            </Box>
        </Box>
    </Box>
);

/**
 * The folio view's lines across its width: fixed 40px rows under a column header that stays put while
 * they scroll, then "Add line" - or a finalized folio's payment - and the summary, right below the
 * last line, held at the bottom only while the lines overflow. While a folio's lines load, the column
 * header stays with a thin bar under it, above room for a folio's usual lines and totals; a folio
 * without lines shows only a small empty state - no header, no totals.
 */
const FolioLinesEditor = ({
    draft, editable, currency, services, reservationService, vatRequired, hiddenIds, referenceDay, newLine, storedTotalCents, loadError, onRetry,
    payment, pendingTipCents,
}: Props) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const taxRates = useAllTaxRates();
    const categories = useAllServiceCategories();
    const format = useFolioFormat(currency);
    const [discountKey, setDiscountKey] = useState<string>();
    const [marketCodeKey, setMarketCodeKey] = useState<string>();
    const [focusKey, setFocusKey] = useState<string>();
    // The latest pick per line: a slow rate must not overwrite a later choice
    const picks = useRef(new Map<string, number>());
    const errorMessage = useMemo(() => (draft.error ? getErrorMessage(draft.error, '') : ''), [draft.error]);
    // The tip a signing posts, a line added elsewhere: highlighted as they arrive
    const arrivals = useLineArrivals(draft.lines, draft.ready, draft.saving);

    const visible = draft.lines.filter(line => !line.item.id || !hiddenIds.has(line.item.id));
    // The reservation's own line: the first saved one
    const primaryKey = draft.lines.find(line => !!line.item.id)?.key;
    const totals = folioTotals(draft.lines.map(line => line.item));
    const serviceOf = (serviceId?: string) => (serviceId ? services.find(service => service.id === serviceId)
        ?? (reservationService?.id === serviceId ? reservationService : undefined) : undefined);

    const pickService = async (line: DraftLine, service: Service) => {
        const pick = (picks.current.get(line.key) ?? 0) + 1;
        picks.current.set(line.key, pick);
        draft.touch(line.key);

        // The choice shows at once; price and VAT follow with the rate
        draft.update(line.key, {description: service.name ?? '', serviceId: service.id});
        const start = line.item.timeSpanStart ? new Date(line.item.timeSpanStart) : newLine.start;
        let rate: LineRate = {};
        try {
            rate = await fetchLineRate(queryClient, service, start);
        } catch (e) {
            console.error('Error loading the service rate:', e);
        }
        if (picks.current.get(line.key) !== pick) return;
        draft.update(line.key, serviceAutofill({service, ...rate, taxRates, start, currency}).patch);
    };

    const addLine = () => setFocusKey(draft.add({...newLine, currency}));
    const discountLine = draft.lines.find(line => line.key === discountKey);
    const marketCodeLine = draft.lines.find(line => line.key === marketCodeKey);

    // A folio's lines load: the column header stays, with a thin bar and room for the lines
    const loading = !draft.ready && !loadError;
    const empty = draft.ready && !visible.length;
    // Only over lines, or where they are about to appear
    const showsLines = loading || visible.length > 0;

    return (
        // Room between the dialog's header and the lines' own
        <Box sx={{flex: {md: '1 1 auto'}, minHeight: {md: 0}, display: 'flex', flexDirection: 'column', pt: 2}}>
            {/* One scroll area: the column header sticks to its top, the summary to its bottom */}
            <Box sx={{flex: {md: '1 1 auto'}, minHeight: {md: 0}, overflow: 'auto', px: {xs: 2, md: 3}}}>
                {!!draft.error && (
                    <Alert
                        severity={'error'}
                        sx={{mb: 1.5}}
                        action={<Button color={'inherit'} size={'small'} onClick={draft.discard}>{t('Discard changes')}</Button>}
                    >
                        {[t('The changes could not be saved.'), errorMessage].filter(Boolean).join(' ')}
                    </Alert>
                )}

                {showsLines && (
                    <Box sx={{minWidth: FOLIO_LINES_MIN_WIDTH}}>
                        <Box
                            sx={{
                                ...GRID_SX,
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                // The app's table header (ServerTable): its gray, its type, its rule
                                height: 38,
                                bgcolor: 'grey.50',
                                borderBottom: `1px solid ${CommonThemeColors.ref.neutral.neutral95}`,
                            }}
                        >
                            {FOLIO_COLUMNS.map(({label, align}) => (
                                <Box
                                    key={label}
                                    // Inset on the side it aligns with the cells' text only: "Menge" fits the Qty column
                                    sx={{
                                        pl: align === 'left' ? 1 : 0,
                                        pr: align === 'right' ? 1 : 0,
                                        typography: 'body1',
                                        fontWeight: 500,
                                        textAlign: align,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {/* Price and Total name the folio's currency: the cells show the amount alone */}
                                    {t(label, {currency: format.currencyCode})}
                                </Box>
                            ))}
                            {loading && <LinearProgress aria-label={t('Loading')} sx={LOADING_BAR_SX}/>}
                        </Box>
                        {loading && <LinesPlaceholder/>}

                        {visible.map(line => (
                            <FolioLineRow
                                key={line.key}
                                line={line}
                                service={serviceOf(line.item.serviceId)}
                                services={services}
                                taxRates={taxRates}
                                categories={categories}
                                format={format}
                                currency={currency}
                                referenceDay={referenceDay}
                                editable={editable}
                                primary={line.key === primaryKey}
                                vatRequired={vatRequired}
                                showErrors={!!line.item.id || draft.showsErrors(line.key)}
                                autoFocus={line.key === focusKey}
                                arrived={arrivals.keys.has(line.key)}
                                onChange={patch => draft.update(line.key, patch)}
                                onPickService={service => pickService(line, service)}
                                onTouch={() => draft.touch(line.key)}
                                onDiscount={() => setDiscountKey(line.key)}
                                onMarketCode={() => setMarketCodeKey(line.key)}
                                onDuplicate={() => draft.duplicate(line.key)}
                                onRemove={() => draft.remove(line.key)}
                            />
                        ))}
                        {/* Where the posted tip line will be: after the lines, once they are there */}
                        {draft.ready && !!pendingTipCents && <FolioPendingTipRow cents={pendingTipCents} format={format}/>}
                    </Box>
                )}

                {!draft.ready && loadError && (
                    <Alert
                        severity={'warning'}
                        sx={{...STAY_IN_VIEW_SX, my: 1.5}}
                        action={onRetry && <Button color={'inherit'} size={'small'} onClick={onRetry}>{t('Retry')}</Button>}
                    >
                        {t('The folio could not be loaded.')}
                    </Alert>
                )}

                {/* A folio without lines yet: a receipt icon and one line, never an empty grid */}
                {empty && (
                    <Box sx={{...STAY_IN_VIEW_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4, textAlign: 'center'}}>
                        <ReceiptLongOutlinedIcon aria-hidden sx={{fontSize: 28, color: 'text.disabled'}}/>
                        <Box sx={{fontSize: 13, color: 'text.secondary'}}>{t('No lines yet')}</Box>
                        {editable && <Button size={'small'} startIcon={<AddIcon/>} onClick={addLine}>{t('Add line')}</Button>}
                    </Box>
                )}

                {/* Right below the last line; held at the bottom while the lines overflow. Its top
                    border lies on the last line's, and becomes the edge the lines scroll under */}
                {draft.ready && !empty && (
                    <Box
                        sx={{
                            ...STAY_IN_VIEW_SX,
                            bottom: 0,
                            zIndex: 1,
                            // The lines' own columns, so the amounts end where the Total column's do -
                            // not at the row menu beyond it
                            ...GRID_SX,
                            minWidth: FOLIO_LINES_MIN_WIDTH,
                            alignItems: 'start',
                            // A payment above the totals, on a phone
                            rowGap: 2,
                            mt: '-1px',
                            // Room between the last line and the totals
                            pt: 2.5,
                            pb: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                        }}
                    >
                        {/* A payment's form needs its room: on a phone it takes the row, the totals go below */}
                        <Box sx={{gridColumn: payment && !editable ? {xs: '1 / -1', md: '1 / 3'} : '1 / 3'}}>
                            {editable ? (
                                <Button startIcon={<AddIcon/>} onClick={addLine} sx={{minHeight: 36}}>
                                    {t('Add line')}
                                </Button>
                            ) : payment}
                        </Box>
                        {/* Up to the Total column, inset like its cells' text */}
                        <Box sx={{gridColumn: '3 / -2', justifySelf: 'end', pr: 1}}>
                            <FolioSummary
                                totals={totals}
                                format={format}
                                storedTotalCents={storedTotalCents}
                                arrivals={arrivals}
                                pendingTipCents={pendingTipCents}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            {discountLine && (
                <FolioDiscountDialog
                    line={discountLine.item}
                    currency={currency}
                    onApply={discounts => {
                        draft.update(discountLine.key, {discounts});
                        setDiscountKey(undefined);
                    }}
                    onClose={() => setDiscountKey(undefined)}
                />
            )}
            {marketCodeLine && (
                <FolioMarketCodeDialog
                    line={marketCodeLine.item}
                    onApply={serviceInventoryCode => {
                        draft.update(marketCodeLine.key, {serviceInventoryCode});
                        setMarketCodeKey(undefined);
                    }}
                    onClose={() => setMarketCodeKey(undefined)}
                />
            )}
        </Box>
    );
};

export default FolioLinesEditor;
